import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import * as S from './Logs.styled';
import { useLogs, useLogsSelector, useWindowSize } from './hooks';
import Loader from 'pages/components/Loader/Loader';
import LogTable from './components/LogTable';
import TmpPanel from 'pages/components/TmpPanel/TmpPanel';
import QuickOptions from './components/QuickOptions';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  logKeys0, logTypes0, selectLogsChecked, setAssociateElevatedAudits, setComplianceAudit,
  setLogSearch, setLogSearchOptions, setLogSearches, setLogsChecked, setLogsValueMode,
  setRecentLog, setSingleLogTime, setAuditsViewAs, setAuditsOnlyLogIds, setAuditsStartEndLogIds,
  setAuditsCombineSku, setAuditsTypeAttributeMode, selectLogs, DEFAULT_LOG_SEARCH_OPTIONS,
  selectIsAuditsReportLogsFocus,
} from './redux';
import { canUseAuditTools } from 'pages/Settings/redux';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { AuditSelector } from './components/AuditsSelector';
import { ModalParts } from 'pages/redux';
import Modal from 'pages/components/Modal/Modal';
import { consumeLogsJump, parseLogsJumpFromSearch, peekLogsJump } from './logUtils';
import { useLocation } from 'react-router-dom';

export const logTypes = logTypes0;
export type LogType = typeof logTypes[number];

export const logKeys = logKeys0;
export type LogKey = typeof logKeys[number];

export type QuickLinks = {
  name: string,
  func: Function
}

export type LogGroups = {
  Hospitality: (
    'Desk reservation' | 'Online reservation' | 'Reservation cancelled' | 'Reservation change'
  )[],
  Departments: string[],
  'Department entries': ('Direct' | 'Expense' | 'Document')[],
  Inventory: string[],
  'Inventory Group': string[],
  // Desk, walk-ins, config, and inventory lifecycle events
  Actions: (
    'Staff logged in' | 'Meal delivered' | 'Housekeeping' | 'Check in' | 'Desk check-in' | 'Check out' | 'Room Actions' | 'Key card access' | 'Room charge' |
    'Low-level favorable' | 'Low-level note' | 'Low-level problem' |
    'Walk in' | 'Walk out' |
    'Rate Plan change' | 'Staff added' | 'Staff change' | 'Staff removed' | 'Settings change' |
    'CRM campaign added' | 'CRM campaign removed' | 'CRM campaign activated' | 'CRM campaign deactivated' |
    'CRM campaign changed' | 'CRM campaign graphic uploaded' | 'CRM campaign graphic deleted' |
    'CRM discount changed' | 'CRM email routing changed' | 'Room added' |
    'Room put on hold' | 'Room hold removed' | 'Room renamed' | 'Room floor changed' | 'Room perks changed' | 'Room type changed' |
    'Room deleted' | 'Audit change' | 'Audit deleted' | 'Audit restored' |
    'Inventory Added' | 'Inventory Renamed' | 'Inventory deleted' | 'Report closed'
  )[],
}

export const logGroupsRaw: LogGroups = {
  Hospitality: [
    'Desk reservation', 'Online reservation', 'Reservation cancelled', 'Reservation change',
  ],
  Departments: [],
  'Department entries': ['Direct', 'Expense', 'Document'],
  Inventory: [],
  'Inventory Group': [],
  Actions: [
    'Staff logged in', 'Check in', 'Desk check-in', 'Check out', 'Meal delivered', 'Housekeeping', 'Room Actions', 'Key card access', 'Room charge',
    'Low-level favorable', 'Low-level note', 'Low-level problem',
    'Walk in', 'Walk out',
    'Rate Plan change', 'Staff added', 'Staff change', 'Staff removed', 'Settings change',
    'CRM campaign added', 'CRM campaign removed', 'CRM campaign activated', 'CRM campaign deactivated',
    'CRM campaign changed', 'CRM campaign graphic uploaded', 'CRM campaign graphic deleted',
    'CRM discount changed', 'CRM email routing changed', 'Room added',
    'Room put on hold', 'Room hold removed', 'Room renamed', 'Room floor changed', 'Room perks changed', 'Room type changed',
    'Room deleted', 'Audit change', 'Audit deleted', 'Audit restored',
    'Inventory Added', 'Inventory Renamed', 'Inventory deleted',
    'Report closed',
  ],
};

const Logs = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isLoadingLogs } = useLogs();
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState<ModalParts | null>(null);

  const triggerAllItems0 = () => {
    setTriggerAllItems(!triggerAllItems);
  };

  const triggerAllItems1 = (key: LogKey, logType: string) => {
    // Room charge is Actions-grouped but still a cash inflow type
    if (logType === 'Room charge') {
      setTriggerCashIn(!triggerCashIn);
      return;
    }
    if (key !== 'Hospitality' && key !== 'Departments' && key !== 'Department entries'
      && key !== 'Inventory' && key !== 'Inventory Group') {
      setTriggerAllItems(!triggerAllItems);
      return;
    }
    if (logType === 'Online reservation' || logType === 'Desk reservation') {
      setTriggerCashIn(!triggerCashIn);
      return;
    }
    if (logType === 'Reservation change') {
      setTriggerCashIn(!triggerCashIn);
      setTriggerCashOut(!triggerCashOut);
      return;
    }
    if (logType === 'Reservation cancelled') {
      setTriggerCashOut(!triggerCashOut);
      return;
    }
    if (key === 'Departments' || key === 'Department entries'
      || key === 'Inventory' || key === 'Inventory Group') {
      setTriggerCashIn(!triggerCashIn);
      setTriggerCashOut(!triggerCashOut);
    }
  };

  const {
    onClickAuditKey, onClickAudit, onClickShowAll
  } = useLogsSelector({
    triggerAllItems0,
    triggerAllItems1,
  });
  const checked = useAppSelector(selectLogsChecked);
  const permission = useAppSelector(selectPermission);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const liveLogs = useAppSelector(selectLogs);
  const isReportLogsFocus = useAppSelector(selectIsAuditsReportLogsFocus);
  const [triggerAllItems, setTriggerAllItems] = useState<boolean>(false);
  const [triggerCashIn, setTriggerCashIn] = useState<boolean>(false);
  const [triggerCashOut, setTriggerCashOut] = useState<boolean>(false);

  const origQuickLinks: QuickLinks[] = [
    {
      name: 'Show all',
      func: (check: QuickLinks['name'][]) => {
        onClickShowAll(check);
        triggerAllItems0();
      }
    },
    {
      name: 'Hospitality',
      func: (check: QuickLinks['name'][]) => {
        onClickAuditKey('Hospitality', check);
        triggerAllItems0();
      }
    },
    {
      name: 'Departments',
      func: (check: QuickLinks['name'][]) => {
        onClickAuditKey('Departments', check);
        triggerAllItems0();
      }
    },
    {
      name: 'Actions',
      func: (check: QuickLinks['name'][]) => {
        onClickAuditKey('Actions', check);
        triggerAllItems0();
      }
    },
    {
      name: 'Desk reservation',
      func: (check: QuickLinks['name'][]) => {
        onClickAudit('Hospitality', 'Desk reservation', check);
        triggerAllItems1('Hospitality', 'Desk reservation');
      }
    },
  ];

  const startQLinks = [...origQuickLinks];
  startQLinks.splice(1, 4);
  const [quickLinks, setQuickLinks] = useState<QuickLinks[]>(startQLinks);
  const { width: windowW } = useWindowSize();

  useEffect(() => {
    const qLinks = [...origQuickLinks];
    if (windowW) {
      if (windowW <= 403) {
        qLinks.splice(1, 4);
        setQuickLinks(qLinks);
        return;
      }
      if (windowW < 577) {
        qLinks.splice(2, 3);
        setQuickLinks(qLinks);
        return;
      }
      if (windowW < 768) {
        qLinks.splice(3, 2);
        setQuickLinks(qLinks);
        return;
      }
      if (windowW < 916) {
        qLinks.splice(4, 1);
        setQuickLinks(qLinks);
      } else {
        setQuickLinks(qLinks);
      }
    }
  }, [windowW]);

  useEffect(() => {
    return () => {
      // Clear session-only Audits modes when leaving — keep time range for return visits.
      dispatch(setAssociateElevatedAudits(false));
      dispatch(setComplianceAudit({ active: false }));
      dispatch(setAuditsViewAs(null));
      dispatch(setAuditsOnlyLogIds(null));
      dispatch(setAuditsStartEndLogIds(null));
    };
  }, [dispatch]);

  // Sessions → /audits always sends elevate=1. Hold the table until jump filters apply.
  const pendingSessionsJump = useMemo(() => {
    const jump = parseLogsJumpFromSearch(location.search);
    return Boolean(jump?.elevate);
  }, [location.search]);
  const [sessionsJumpReady, setSessionsJumpReady] = useState(!pendingSessionsJump);

  useLayoutEffect(() => {
    const fromUrl = parseLogsJumpFromSearch(location.search);
    const stashed = peekLogsJump();
    const jump = fromUrl || consumeLogsJump();
    if (!jump) {
      setSessionsJumpReady(true);
      return undefined;
    }
    // URL may omit `only` — keep the exact chip ids from the report click.
    if (!jump.onlyLogIds?.length && stashed?.onlyLogIds?.length) {
      jump.onlyLogIds = stashed.onlyLogIds;
    }
    dispatch(setLogsChecked(jump.checked));
    dispatch(setLogSearches([]));
    dispatch(setLogSearch(jump.search || ''));
    dispatch(setLogSearchOptions({
      ...DEFAULT_LOG_SEARCH_OPTIONS,
      negate: Boolean(jump.searchNegate),
      matchMode: jump.searchMatchMode || 'any',
      exactMatch: Boolean(jump.searchExactMatch),
      hideArchives: Boolean(jump.hideArchives),
    }));
    dispatch(setAssociateElevatedAudits(Boolean(jump.elevate)));
    dispatch(setComplianceAudit({
      // Do not treat elevate-alone as associated-cashier mode (Sessions on-hand
      // is a department float and must keep every cashier in the window).
      active: Boolean(jump.isComplianceAudit),
      staffId: jump.cashierId || null,
      username: jump.cashierUsername || null,
    }));
    dispatch(setLogsValueMode(jump.valueMode === 'units' ? 'units' : 'value'));
    dispatch(setAuditsStartEndLogIds(
      jump.startLogId || jump.endLogId
        ? { startLogId: jump.startLogId, endLogId: jump.endLogId }
        : null,
    ));
    // Time range is filled from the start/end log ids when those logs are loaded.
    const startLog = jump.startLogId
      ? (liveLogs || []).find((log) => String(log.id) === String(jump.startLogId))
      : null;
    const endLog = jump.endLogId
      ? (liveLogs || []).find((log) => String(log.id) === String(jump.endLogId))
      : null;
    const startMs = startLog
      ? +new Date(startLog.date)
      : (jump.startDate ? +new Date(jump.startDate) : NaN);
    const endMs = endLog
      ? +new Date(endLog.date)
      : (jump.endDate ? +new Date(jump.endDate) : NaN);
    if (Number.isFinite(startMs) || Number.isFinite(endMs)) {
      const fromMs = Number.isFinite(startMs) ? startMs : endMs;
      const toMs = Number.isFinite(endMs) ? endMs : startMs;
      dispatch(setSingleLogTime({
        startDate: new Date(Math.min(fromMs, toMs)),
        endDate: new Date(Math.max(fromMs, toMs)),
      }));
    }
    // Staff report Show logs: temporarily use the other party's Audits visibility.
    const viewAsIsMe = Boolean(
      jump.viewAsPermission != null
      && (
        (staffId && jump.viewAsStaffId && String(jump.viewAsStaffId) === String(staffId))
        || (
          username
          && jump.viewAsUsername
          && String(jump.viewAsUsername).trim().toLowerCase()
            === String(username).trim().toLowerCase()
        )
      ),
    );
    if (
      !viewAsIsMe
      && jump.viewAsPermission != null
      && Number.isFinite(Number(jump.viewAsPermission))
    ) {
      dispatch(setAuditsViewAs({
        permission: Number(jump.viewAsPermission),
        staffId: jump.viewAsStaffId || null,
        username: jump.viewAsUsername || null,
        label: jump.viewAsLabel || null,
      }));
    } else {
      dispatch(setAuditsViewAs(null));
    }
    // Exact chip / Total ids — survive remount (redux is cleared on leave).
    dispatch(setAuditsOnlyLogIds(jump.onlyLogIds?.length ? jump.onlyLogIds : null));
    dispatch(setAuditsCombineSku(Boolean(jump.combineSku)));
    // Restore the Audit-header attribute the clicked report logs were shown with.
    if (jump.typeAttributeMode) {
      dispatch(setAuditsTypeAttributeMode(jump.typeAttributeMode));
    }
    // Optional: land on a specific audit after filters apply.
    if (jump.focusLogId) {
      dispatch(setRecentLog(String(jump.focusLogId)));
    }
    // Sessions only: keep loader up for a paint so the unfiltered table never flashes.
    if (!jump.elevate) {
      setSessionsJumpReady(true);
      return undefined;
    }
    setSessionsJumpReady(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSessionsJumpReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [dispatch, location.search, staffId, username]);

  const showSessionsJumpLoader = pendingSessionsJump && !sessionsJumpReady;
  const showLogsLoader = isLoadingLogs || isLoading || showSessionsJumpLoader;

  return (
    <S.Container>
      {showLogsLoader && (
        <Loader message={isLoading ? 'Saving…' : 'Loading logs…'} />
      )}
      {
        !!modal && (
          <Modal
            component={modal.component}
            exitOnBgClick={modal.exitOnBgClick}
          />
        )
      }
      {
        canUseAuditTools(permission) && sessionsJumpReady ? (
          <>
            <TmpPanel component={<AuditSelector />} id="tmp_panel0" />
            <AuditSelector isWeb />
            <S.AuditsPanelMobile $hidden={isReportLogsFocus}>
              <S.AuditsText>Audits</S.AuditsText>
              <QuickOptions
                quickLinks={quickLinks}
                checked={checked}
                buttonLabel="Select Audit"
              />
            </S.AuditsPanelMobile>
          </>
        ) : (null)
      }
      {
        !isLoadingLogs && sessionsJumpReady && (
          <LogTable
            checked={checked}
            triggerAllItems={triggerAllItems}
            triggerCashIn={triggerCashIn}
            triggerCashOut={triggerCashOut}
            setModal={setModal}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
          />
        )
      }
    </S.Container>
  );
};

export default Logs;
